/**
 * ActivityTimeline - Task activity/history feed (Jira-like)
 * 
 * Shows chronological list of:
 * - Status changes
 * - Assignments
 * - Field updates
 * - Comments
 */

import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Clock,
  User,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  Edit,
  MessageSquare,
  Camera,
  FileText,
} from 'lucide-react';

/**
 * Get icon and color for activity type
 */
const getActivityMeta = (type, theme) => {
  switch (type) {
    case 'created':
      return { icon: FileText, color: theme.palette.primary.main, label: 'created this task' };
    case 'status_change':
    case 'status_changed':
      return { icon: CheckCircle, color: theme.palette.info.main, label: 'changed status' };
    case 'assigned':
      return { icon: User, color: theme.palette.secondary.main, label: 'assigned' };
    case 'unassigned':
      return { icon: User, color: theme.palette.warning.main, label: 'unassigned' };
    case 'completed':
      return { icon: CheckCircle, color: theme.palette.success.main, label: 'completed this task' };
    case 'cancelled':
      return { icon: XCircle, color: theme.palette.error.main, label: 'cancelled this task' };
    case 'stage_completed':
      return { icon: CheckCircle, color: theme.palette.success.main, label: 'completed a stage' };
    case 'stage_in_progress':
      return { icon: Play, color: theme.palette.info.main, label: 'started a stage' };
    case 'stage_skipped':
      return { icon: Pause, color: theme.palette.warning.main, label: 'skipped a stage' };
    case 'stage_pending':
      return { icon: Clock, color: theme.palette.grey[500], label: 'reset stage' };
    case 'field_updated':
      return { icon: Edit, color: theme.palette.secondary.main, label: 'updated fields' };
    case 'photos_uploaded':
      return { icon: Camera, color: theme.palette.primary.main, label: 'uploaded photos' };
    case 'in_progress':
      return { icon: Play, color: theme.palette.info.main, label: 'started work' };
    case 'comment':
      return { icon: MessageSquare, color: theme.palette.text.secondary, label: 'commented' };
    case 'photo_added':
      return { icon: Camera, color: theme.palette.primary.main, label: 'added a photo' };
    case 'field_update':
      return { icon: Edit, color: theme.palette.text.secondary, label: 'updated' };
    default:
      return { icon: Clock, color: theme.palette.text.secondary, label: 'updated' };
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Single activity item
 */
const ActivityItem = ({ activity, isLast }) => {
  const theme = useTheme();
  const meta = getActivityMeta(activity.type, theme);
  const Icon = meta.icon;
  
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        position: 'relative',
        pb: isLast ? 0 : 2,
      }}
    >
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            top: 28,
            bottom: 0,
            width: 2,
            backgroundColor: theme.palette.divider,
          }}
        />
      )}
      
      {/* Icon */}
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          backgroundColor: alpha(meta.color, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <Icon size={14} color={meta.color} />
      </Box>
      
      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {activity.user_name || 'System'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: theme.palette.text.secondary,
            }}
          >
            {meta.label}
          </Typography>
          {activity.details && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: theme.palette.text.primary,
              }}
            >
              {activity.details}
            </Typography>
          )}
        </Box>
        
        {/* Status change badge */}
        {activity.type === 'status_change' && activity.from_status && activity.to_status && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <StatusBadge status={activity.from_status} theme={theme} />
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>→</Typography>
            <StatusBadge status={activity.to_status} theme={theme} />
          </Box>
        )}
        
        {/* Comment content */}
        {activity.type === 'comment' && activity.content && (
          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 1,
              fontSize: '0.8125rem',
              color: theme.palette.text.primary,
            }}
          >
            {activity.content}
          </Box>
        )}
        
        {/* Timestamp */}
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: theme.palette.text.disabled,
            mt: 0.25,
          }}
        >
          {formatRelativeTime(activity.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Status badge component
 */
const StatusBadge = ({ status, theme }) => {
  const getStatusColor = (s) => {
    switch (s?.toLowerCase()) {
      case 'completed': return theme.palette.success.main;
      case 'in_progress': return theme.palette.info.main;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.warning.main;
    }
  };
  
  const getStatusLabel = (s) => {
    switch (s?.toLowerCase()) {
      case 'in_progress': return 'In Progress';
      case 'pending': return 'Pending';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return s || 'Unknown';
    }
  };
  
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: alpha(getStatusColor(status), 0.1),
        fontSize: '0.75rem',
        fontWeight: 500,
        color: getStatusColor(status),
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: getStatusColor(status),
        }}
      />
      {getStatusLabel(status)}
    </Box>
  );
};

/**
 * ActivityTimeline Component
 */
const ActivityTimeline = ({ 
  activities = [], 
  loading = false,
  taskData = null,
}) => {
  const theme = useTheme();
  
  // Generate activities from task data if no activities provided
  const displayActivities = activities.length > 0 ? activities : generateActivitiesFromTask(taskData);
  
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Skeleton variant="circular" width={26} height={26} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={16} />
              <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  
  if (displayActivities.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          color: theme.palette.text.secondary,
          fontSize: '0.875rem',
        }}
      >
        No activity recorded
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="overline"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          letterSpacing: 1,
          mb: 2,
          display: 'block',
        }}
      >
        Activity
      </Typography>
      
      {displayActivities.map((activity, index) => (
        <ActivityItem
          key={activity.id || index}
          activity={activity}
          isLast={index === displayActivities.length - 1}
        />
      ))}
    </Box>
  );
};

/**
 * Generate activity items from task data when no activity history is available
 */
const generateActivitiesFromTask = (taskData) => {
  if (!taskData) return [];
  
  const activities = [];
  
  // Created activity
  if (taskData.created_at) {
    activities.push({
      id: 'created',
      type: 'created',
      user_name: taskData.created_by_name || 'System',
      created_at: taskData.created_at,
    });
  }
  
  // Assignment activity
  if (taskData.assigned_to && taskData.staff_name) {
    activities.push({
      id: 'assigned',
      type: 'assigned',
      user_name: 'Admin',
      details: taskData.staff_name,
      created_at: taskData.assigned_at || taskData.updated_at || taskData.created_at,
    });
  }
  
  // Status change if not pending
  const status = taskData.status?.toLowerCase();
  if (status && status !== 'pending') {
    activities.push({
      id: 'status',
      type: status === 'completed' ? 'completed' : status === 'cancelled' ? 'cancelled' : 'status_change',
      user_name: taskData.staff_name || 'Staff',
      from_status: 'pending',
      to_status: status,
      created_at: taskData.completed_at || taskData.updated_at,
    });
  }
  
  // Sort by date descending (newest first)
  return activities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

export default ActivityTimeline;
